import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from '@/store/reducers';
import { setTemporaryChanges } from '@/store/actions/projectdata.action';
import CommonService from '@/services/common.service';
import { injectCssIntoHtmlHead } from '../utils/content-extractors';

export const useGrapesJSManualSave = (selectedHtmlFile: string) => {
    const dispatch = useDispatch();
    const tempCode = useSelector((state: IRootReducerState) => state.projectData.tempCode);

    // Handle save from GrapesJS (permanent updates)
    const handleGrapesJSSave = useCallback(async (updatedHtml: string, updatedCss?: string) => {        
        // Show loading state
        
        // Create a temporary loading overlay
        const loadingOverlay = document.createElement('div');
        loadingOverlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            color: white;
            font-family: 'Inter', sans-serif;
            border-radius: 8px;
        `;
        
        loadingOverlay.innerHTML = `
            <div style="
                background: #1f2937;
                padding: 30px;
                border-radius: 12px;
                border: 1px solid #374151;
                text-align: center;
                max-width: 400px;
                box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
            ">
                <div style="
                    width: 40px;
                    height: 40px;
                    border: 3px solid #374151;
                    border-top: 3px solid #10b981;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 20px;
                "></div>
                <h3 style="margin: 0 0 10px; color: #f9fafb; font-size: 18px;">Saving Changes</h3>
                <p style="margin: 0; color: #d1d5db; font-size: 14px;">Processing visual editor updates...</p>
                <div style="
                    margin-top: 15px;
                    padding: 10px;
                    background: #374151;
                    border-radius: 6px;
                    font-size: 12px;
                    color: #9ca3af;
                ">
                    <div>✓ Extracting HTML content</div>
                    <div>✓ Processing CSS styles</div>
                    <div>⏳ Updating project files</div>
                    <div>⏳ Preparing commit</div>
                </div>
            </div>
            <style>
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            </style>
        `;
        
        // Find the preview container and append the overlay to it
        const previewContainer = document.querySelector('.sandpack-html-preview-content') as HTMLElement;
        if (previewContainer) {
            previewContainer.style.position = 'relative';
            previewContainer.appendChild(loadingOverlay);
        } else {
            // Fallback to body if preview container not found
            document.body.appendChild(loadingOverlay);
        }
        
        try {
            // Parse the current temp code to get existing files
            const currentCode = tempCode || '{}';
            const parsedCode = JSON.parse(currentCode);
            
            // Get the current HTML file content
            const currentHtmlFile = parsedCode[selectedHtmlFile];
            const currentHtmlContent = typeof currentHtmlFile === 'string' ? currentHtmlFile : currentHtmlFile?.code || '';
            
            // Extract body content from the updated HTML
            let bodyContent = updatedHtml;
            const bodyMatch = updatedHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
            if (bodyMatch) {
                bodyContent = bodyMatch[1];
            }
            
            // Replace only the body content in the original HTML, preserving head and other parts
            let updatedHtmlContent = currentHtmlContent;
            const originalBodyMatch = currentHtmlContent.match(/<body[^>]*>[\s\S]*?<\/body>/i);
            
            if (originalBodyMatch) {
                // Replace only the body content while preserving the original body tag attributes
                const originalBodyTag = originalBodyMatch[0];
                const bodyTagMatch = originalBodyTag.match(/<body[^>]*>/);
                const originalBodyAttributes = bodyTagMatch ? bodyTagMatch[0] : '<body>';
                
                updatedHtmlContent = currentHtmlContent.replace(
                    /<body[^>]*>[\s\S]*?<\/body>/i,
                    `${originalBodyAttributes}${bodyContent}</body>`
                );
            } else {
                // If no body tag exists, check if it's a complete HTML document
                if (currentHtmlContent.includes('<html') && currentHtmlContent.includes('</html>')) {
                    // Insert body content before closing html tag
                    updatedHtmlContent = currentHtmlContent.replace(
                        /<\/html>/i,
                        `<body>${bodyContent}</body></html>`
                    );
                } else {
                    // If it's not a complete HTML document, wrap the content in body tags
                    updatedHtmlContent = `<body>${bodyContent}</body>`;
                }
            }
            
            // Update the selected HTML file with the new content in Sandpack format
            parsedCode[selectedHtmlFile] = {
                code: updatedHtmlContent,
                active: true
            };
            
            // Update CSS file if CSS content is provided
            if (updatedCss && updatedCss.trim()) {
                // Find the main CSS file (usually style.css or index.css)
                const cssFiles = Object.keys(parsedCode).filter(file => file.endsWith('.css'));
                const mainCssFile = cssFiles.find(f => f.includes('style') || f.includes('index')) || cssFiles[0];
                
                if (mainCssFile) {
                    parsedCode[mainCssFile] = {
                        code: updatedCss,
                        active: true
                    };
                } else {
                    // Create a new CSS file if none exists
                    parsedCode['style.css'] = {
                        code: updatedCss,
                        active: true
                    };
                }
                
                // Inject CSS into the HTML file to ensure it's properly included
                const htmlWithCss = injectCssIntoHtmlHead(updatedHtmlContent, updatedCss);
                parsedCode[selectedHtmlFile] = {
                    code: htmlWithCss,
                    active: true
                };
            }
            
            // Convert back to string and dispatch to Redux
            const updatedCode = JSON.stringify(parsedCode);
            dispatch(setTemporaryChanges(updatedCode));
            
            
            // Automatically switch back to preview mode after saving
            
            // Show success message briefly
            setTimeout(() => {
                loadingOverlay.innerHTML = `
                    <div style="
                        background: #1f2937;
                        padding: 30px;
                        border-radius: 12px;
                        border: 1px solid #10b981;
                        text-align: center;
                        max-width: 400px;
                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                    ">
                        <div style="
                            width: 40px;
                            height: 40px;
                            background: #10b981;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin: 0 auto 20px;
                            font-size: 20px;
                        ">✓</div>
                        <h3 style="margin: 0 0 10px; color: #f9fafb; font-size: 18px;">Changes Saved!</h3>
                        <p style="margin: 0; color: #d1d5db; font-size: 14px;">Visual editor changes have been saved successfully.</p>
                        <div style="
                            margin-top: 15px;
                            padding: 10px;
                            background: #374151;
                            border-radius: 6px;
                            font-size: 12px;
                            color: #9ca3af;
                        ">
                            <div>✓ HTML content updated</div>
                            <div>✓ CSS styles processed</div>
                            <div>✓ Project files updated</div>
                            <div>✓ Ready for commit</div>
                        </div>
                    </div>
                `;
                
                // Remove loading overlay after 2 seconds
                setTimeout(() => {
                    if (loadingOverlay.parentNode) {
                        loadingOverlay.parentNode.removeChild(loadingOverlay);
                    }
                }, 2000);
            }, 1000);
            
            // Trigger RxJS event for automatic save with default commit message
            CommonService._communications.triggerGrapesJSSaveSubject.next({
                htmlContent: updatedHtml,
                cssContent: updatedCss,
                commitMessage: "Edited using visual editor"
            });

            
        } catch (error) {
            console.error('Error saving GrapesJS changes:', error);
            
            // Show error message
            loadingOverlay.innerHTML = `
                <div style="
                    background: #1f2937;
                    padding: 30px;
                    border-radius: 12px;
                    border: 1px solid #ef4444;
                    text-align: center;
                    max-width: 400px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                ">
                    <div style="
                        width: 40px;
                        height: 40px;
                        background: #ef4444;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 20px;
                        font-size: 20px;
                    ">✕</div>
                    <h3 style="margin: 0 0 10px; color: #f9fafb; font-size: 18px;">Save Failed</h3>
                    <p style="margin: 0; color: #d1d5db; font-size: 14px;">There was an error saving your changes.</p>
                    <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
                        margin-top: 15px;
                        padding: 8px 16px;
                        background: #ef4444;
                        color: white;
                        border: none;
                        border-radius: 6px;
                        cursor: pointer;
                    ">Close</button>
                </div>
            `;
        }
    }, [tempCode, selectedHtmlFile, dispatch]);

    return { handleGrapesJSSave };
}; 